import React, { useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { FontPoppins } from '@/constants/font';
import { createThemedStyles } from '@/hooks/use-theme-colors';

type Option = { label: string; value: string };

type InlineSelectProps = {
  label: string;
  value: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

export function InlineSelect({
  label,
  value,
  options,
  placeholder = 'Selecione…',
  disabled,
  onChange,
}: InlineSelectProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const insets = useSafeAreaInsets();
  const selected = options.find((o) => o.value === value);

  const handleClose = () => setOpen(false);

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <View style={styles.wrapper}>
      <ThemedText className="mb-1 text-xs text-foreground/60">{label}</ThemedText>
      <Pressable
        onPress={() => !disabled && setOpen(true)}
        disabled={disabled}
        style={[styles.trigger, disabled && styles.triggerDisabled]}
      >
        <Text style={[styles.triggerText, !selected && styles.placeholderText]} numberOfLines={1}>
          {selected?.label ?? placeholder}
        </Text>
        <Ionicons className="text-neutral-500" name="chevron-down" size={16} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
        statusBarTranslucent
      >
        <View style={[styles.overlay, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={handleClose}
            accessibilityLabel="Fechar"
          />
          <View style={styles.dialog} className="border-border bg-card">
            <View className="mb-3 flex-row items-center justify-between border-b border-border pb-3">
              <ThemedText className="font-semibold text-base text-foreground">{label}</ThemedText>
              <Pressable
                onPress={handleClose}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="Fechar seleção"
              >
                <Ionicons className="text-neutral-500" name="close" size={22} />
              </Pressable>
            </View>

            <ScrollView
              style={styles.optionsList}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {options.length === 0 ? (
                <ThemedText className="py-4 text-center text-sm text-foreground/50">
                  Nenhuma opção disponível
                </ThemedText>
              ) : (
                options.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <Pressable
                      key={opt.value}
                      onPress={() => handleSelect(opt.value)}
                      style={[styles.optionRow, isSelected && styles.optionRowSelected]}
                    >
                      {isSelected ? (
                        <Ionicons
                          name="checkmark-circle"
                          size={18}
                          color="#FD5001"
                          style={styles.checkIcon}
                        />
                      ) : (
                        <View style={styles.checkSpacer} />
                      )}
                      <Text
                        style={[styles.optionText, isSelected && styles.optionTextSelected]}
                        numberOfLines={2}
                      >
                        {opt.label}
                      </Text>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const useStyles = createThemedStyles((th) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      minWidth: 0,
    },
    trigger: {
      minHeight: 44,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: th('#e5e7eb'),
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: th('#fff'),
    },
    triggerDisabled: {
      opacity: 0.5,
    },
    triggerText: {
      flex: 1,
      fontSize: 14,
      fontFamily: FontPoppins.REGULAR,
      color: th('#111827'),
      marginRight: 8,
    },
    placeholderText: {
      color: th('#9ca3af'),
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 20,
    },
    dialog: {
      width: '100%',
      maxWidth: 420,
      maxHeight: '75%',
      borderRadius: 12,
      borderWidth: 1,
      padding: 16,
      zIndex: 1,
      ...Platform.select({
        ios: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.2,
          shadowRadius: 8,
        },
        android: { elevation: 8 },
        default: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 12,
        },
      }),
    },
    optionsList: {
      maxHeight: 320,
    },
    optionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 8,
      marginBottom: 4,
    },
    optionRowSelected: {
      backgroundColor: 'rgba(253, 80, 1, 0.08)',
    },
    checkIcon: {
      marginRight: 8,
    },
    checkSpacer: {
      width: 26,
    },
    optionText: {
      flex: 1,
      fontSize: 15,
      fontFamily: FontPoppins.REGULAR,
      color: th('#111827'),
    },
    optionTextSelected: {
      fontFamily: FontPoppins.SEMIBOLD,
      color: '#FD5001',
    },
  }),
);
